import React, { useState } from 'react';
import { useContent } from '../../hooks/useContent';
import { useToast } from '../../hooks/useToast';
import { SpinnerIcon } from '../icons/SpinnerIcon';
import { XIcon } from '../icons/XIcon';
import { ConfirmationModal } from '../ConfirmationModal';

export const RedirectsPanel: React.FC = () => {
    const { redirects, addRedirect, deleteRedirect } = useContent();
    const { showToast } = useToast();

    const [newRedirectSource, setNewRedirectSource] = useState('');
    const [newRedirectDest, setNewRedirectDest] = useState('');
    const [redirectError, setRedirectError] = useState<string | null>(null);
    const [isRedirectLoading, setIsRedirectLoading] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [redirectToDelete, setRedirectToDelete] = useState<string | null>(null);
    
    const handleAddRedirect = async (e: React.FormEvent) => {
        e.preventDefault();
        setRedirectError(null);
        if (!newRedirectSource.trim() || !newRedirectDest.trim()) {
          setRedirectError('Both source and destination are required.');
          return;
        }
        if (!newRedirectSource.startsWith('/')) {
            setRedirectError("Source path must begin with a forward slash '/'.");
            return;
        }
        if (newRedirectSource.trim() === newRedirectDest.trim()) {
          setRedirectError('Source and destination cannot be the same.');
          return;
        }
        if (redirects.some(r => r.source === newRedirectSource.trim())) {
          setRedirectError('A redirect for this source path already exists.');
          return;
        }
        setIsRedirectLoading(true);
        try {
          await addRedirect(newRedirectSource.trim(), newRedirectDest.trim());
          showToast('Redirect added successfully!');
          setNewRedirectSource('');
          setNewRedirectDest('');
        } catch (error) {
          setRedirectError(error instanceof Error ? error.message : 'An unknown error occurred.');
        } finally {
          setIsRedirectLoading(false);
        }
    };
    
    const openDeleteModal = (id: string) => {
        setRedirectToDelete(id);
        setIsDeleteModalOpen(true);
    };
    
    const handleConfirmDelete = async () => {
        if (!redirectToDelete) return;
        try {
          await deleteRedirect(redirectToDelete);
          showToast('Redirect deleted.');
        } catch (error) {
          showToast('Failed to delete redirect.');
        } finally {
          setIsDeleteModalOpen(false);
          setRedirectToDelete(null);
        }
    };

    return (
        <>
            <div className="mt-8">
                <h2 className="text-xl font-bold text-indigo-700 mb-4">Admin: Manage Redirects</h2>
                <div className="bg-indigo-50 rounded-lg p-6 border border-indigo-200 space-y-6">
                    <form onSubmit={handleAddRedirect} className="grid grid-cols-1 sm:grid-cols-5 gap-4 items-end">
                        <div className="sm:col-span-2">
                            <label htmlFor="redirect-source" className="block text-sm font-medium text-gray-700">Source Path</label>
                            <input type="text" id="redirect-source" value={newRedirectSource} onChange={e => setNewRedirectSource(e.target.value)} placeholder="/old-page" className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500" />
                        </div>
                        <div className="sm:col-span-2">
                            <label htmlFor="redirect-dest" className="block text-sm font-medium text-gray-700">Destination URL</label>
                            <input type="text" id="redirect-dest" value={newRedirectDest} onChange={e => setNewRedirectDest(e.target.value)} placeholder="/new-page-or-https://..." className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500" />
                        </div>
                        <button type="submit" disabled={isRedirectLoading} className="w-full flex justify-center items-center gap-2 py-2 px-4 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-md shadow-sm transition-colors disabled:opacity-50">{isRedirectLoading ? <><SpinnerIcon /> Adding...</> : 'Add'}</button>
                    </form>
                    {redirectError && <p className="text-sm text-red-600">{redirectError}</p>}
                    <div className="space-y-2 max-h-72 overflow-y-auto">
                        {redirects.length > 0 ? redirects.map(r => (
                            <div key={r.id} className="bg-white p-3 rounded-md border border-gray-200 flex justify-between items-center text-sm">
                                <div className="flex-1 overflow-hidden">
                                    <p className="font-mono text-gray-800 truncate"><strong>From:</strong> {r.source}</p>
                                    <p className="font-mono text-gray-500 truncate"><strong>To:</strong> {r.destination}</p>
                                </div>
                                <button onClick={() => openDeleteModal(r.id)} className="ml-4 text-red-500 hover:text-red-700" aria-label={`Delete redirect from ${r.source}`}><XIcon className="h-5 w-5" /></button>
                            </div>
                        )) : <p className="text-center text-gray-500">No redirects configured.</p>}
                    </div>
                </div>
            </div>
            <ConfirmationModal
                isOpen={isDeleteModalOpen}
                onClose={() => setIsDeleteModalOpen(false)}
                onConfirm={handleConfirmDelete}
                title="Confirm Redirect Deletion"
                message="Are you sure you want to permanently delete this redirect? This action cannot be undone."
            />
        </>
    );
};